/**
 * Inicio — DESKTOP.
 *
 * No es la mobile estirada. Acá el ancho se usa para poner el KPI principal AL LADO de lo que lo
 * explica: la pila de placas grande a la izquierda y, a la derecha, las tres series que la
 * componen (peso, grasa, masa magra) en el mismo eje temporal. En el celular eso sería un scroll
 * de tres pantallas y la relación entre las curvas se perdería.
 *
 * Las alertas van en columna propia en vez de empujar el contenido hacia abajo, y hay una fila de
 * cifras densa que en mobile no tendría dónde caber.
 *
 * La franja de hoy va arriba y ocupa el ancho. Antes el único botón para abrir una sesión estaba
 * dentro de la cabecera de "Para mirar", en la segunda columna del segundo bloque: la acción
 * principal de la app enterrada entre tarjetas de análisis.
 */

import type { ReactNode } from 'react';
import {
  AVISO_BIOIMPEDANCIA,
  UMBRAL_PRATIO_BAJO,
  bandaFFMI,
  fechaLarga,
  formatearNumero,
  nombreDia,
} from '@mg/core';

import { useInicio } from '../features/useInicio.ts';
import { useRuta } from '../ui/router.tsx';
import { Aviso, Cifra, Insignia, Pila, PilaDePlan, Vacio, estadosDeRatio } from '../ui/componentes.tsx';
import { LineaConMedia } from '../ui/charts.tsx';
import { PrimerosPasos } from '../ui/PrimerosPasos.tsx';

export function InicioDesktop(): ReactNode {
  const v = useInicio();
  const { ir } = useRuta();

  if (v.datos.cargando) return <Vacio>Cargando…</Vacio>;

  const ultimaDerivada = v.composicion.derivadas[v.composicion.derivadas.length - 1] ?? null;
  const banda = ultimaDerivada?.ffmi ? bandaFFMI(ultimaDerivada.ffmi, v.datos.perfil?.sexo ?? 'otro') : null;

  return (
    <>
      <PrimerosPasos datos={v.datos} compacto />

      <section className="banda-hoy banda-hoy--ancha">
        <div>
          <span className="eyebrow">Hoy · {nombreDia(v.datos.hoy)}</span>
          <h2 className="titulo" style={{ marginTop: 2 }}>
            {v.queTocaHoy.titulo}
          </h2>
          <p className="nota" style={{ marginTop: 2 }}>
            {v.queTocaHoy.detalle}
          </p>
        </div>

        {/* Sin la tecla R al lado: en un botón ámbar el <kbd> queda gris sobre naranja y se lee
            peor que en la barra lateral, que ya la muestra.

            El ámbar es sólo para la acción del día: si ya entrenaste, o si hoy no toca, el botón
            va normal. Una franja que grita "entrená" cuando entrenar no es lo que sigue enseña a
            no creerle. */}
        <button
          type="button"
          className={v.queTocaHoy.esPrimaria ? 'boton boton--principal' : 'boton'}
          onClick={() => ir('registro')}
        >
          {v.queTocaHoy.accion}
        </button>
      </section>

      <div className="d-rejilla d-rejilla--heroe">
        <section className="tarjeta">
          <span className="eyebrow">Calidad de la pérdida · últimas 4 semanas</span>

          {v.pratio.estado === 'ok' && v.pratio.calidad !== null ? (
            <>
              <div className="fila" style={{ alignItems: 'baseline', gap: 'var(--e2)', marginTop: 6 }}>
                <span className="cifra-heroe">{Math.round(v.pratio.calidad)}</span>
                <span className="unidad" style={{ fontSize: 24 }}>
                  %
                </span>
                {v.pratio.recomposicion && <Insignia tono="agua">recomposición</Insignia>}
              </div>

              <div style={{ marginTop: 'var(--e4)' }}>
                <Pila
                  estados={estadosDeRatio(
                    v.pratio.calidad / 100,
                    20,
                    v.pratio.calidad >= UMBRAL_PRATIO_BAJO ? 'llena' : 'alerta',
                  )}
                  alto={44}
                  etiqueta={`${Math.round(v.pratio.calidad)} % de lo que bajaste fue grasa`}
                />
                <p className="nota--fina" style={{ marginTop: 6 }}>
                  Cada placa, 5 % de lo que bajaste. Llenas: grasa. Huecas: masa magra.
                </p>
              </div>

              <hr className="separador" />

              <div className="d-rejilla d-rejilla--3" style={{ gap: 'var(--e3)' }}>
                <div>
                  <span className="eyebrow">Bajaste</span>
                  <Cifra valor={v.pratio.pesoPerdidoKg} unidad="kg" />
                </div>
                <div>
                  <span className="eyebrow">De grasa</span>
                  <Cifra valor={v.pratio.grasaPerdidaKg} unidad="kg" />
                </div>
                <div>
                  <span className="eyebrow">De magra</span>
                  <Cifra valor={v.pratio.magraPerdidaKg} unidad="kg" />
                </div>
              </div>

              <p className="nota" style={{ marginTop: 'var(--e3)' }}>
                {v.pratio.detalle} Calculado sobre medias móviles de 7 días, con {v.pratio.lecturas} lecturas.
              </p>
            </>
          ) : (
            <>
              {/* La pila vacía en vez de un guion gigante: comunica "todavía no se puede
                  calcular", no "algo se rompió al cargar". Más baja que la del héroe y con la
                  explicación arriba: hasta que haya número, lo que importa es qué es esto y qué
                  falta para tenerlo, no el hueco donde va a ir. */}
              <p className="nota" style={{ marginTop: 'var(--e3)' }}>
                De todo el peso que bajes, cuánto fue grasa y cuánto músculo. Es el número que esta
                app existe para responder, y es el único que una balanza sola no te puede dar.
              </p>
              <div style={{ marginTop: 'var(--e4)' }}>
                <Pila estados={new Array(20).fill('hueca')} alto={20} etiqueta="Todavía sin calcular" />
              </div>
              <p className="nota--fina" style={{ marginTop: 'var(--e3)' }}>
                {v.pratio.detalle}
              </p>
              <button
                type="button"
                className="boton boton--chico"
                style={{ marginTop: 'var(--e3)' }}
                onClick={() => ir('cuerpo')}
              >
                Registrar una medición
              </button>
            </>
          )}
        </section>

        <div className="columna" style={{ gap: 'var(--e4)' }}>
          <div className="d-rejilla d-rejilla--4">
            <Tarjetita etiqueta="Masa magra" valor={ultimaDerivada?.magraKg ?? null} unidad="kg" />
            <Tarjetita
              etiqueta="FFMI"
              valor={ultimaDerivada?.ffmi ?? null}
              unidad=""
              pie={banda ? banda.etiqueta : v.faltaAltura ? 'falta tu altura' : undefined}
            />
            <Tarjetita
              etiqueta="Velocidad"
              valor={v.velocidad.porcentajeSemanal}
              unidad="%/sem"
              decimales={2}
              alerta={v.velocidad.excedeBanda}
            />
            <Tarjetita
              etiqueta="Volumen 7 d"
              valor={v.volumenSemanaKg / 1000}
              unidad="t"
              pie={`racha de ${v.adherencia.rachaActual}`}
            />
          </div>

          <section className="tarjeta">
            <div className="fila fila--entre" style={{ marginBottom: 'var(--e3)' }}>
              <span className="eyebrow">Peso, grasa y masa magra · media de 7 días</span>
              <span className="nota--fina">los puntos huecos son las lecturas crudas</span>
            </div>

            <div className="d-rejilla d-rejilla--3">
              <div>
                <span className="nota--fina">Peso (kg)</span>
                <LineaConMedia puntos={v.composicion.peso} alto={130} unidad="kg" />
              </div>
              <div>
                <span className="nota--fina">Grasa (kg)</span>
                <LineaConMedia puntos={v.composicion.grasaKg} alto={130} tono="ambar" unidad="kg" />
              </div>
              <div>
                <span className="nota--fina">Masa magra (kg)</span>
                <LineaConMedia puntos={v.composicion.magraKg} alto={130} tono="agua" unidad="kg" />
              </div>
            </div>

            <p className="nota--fina" style={{ marginTop: 'var(--e3)' }}>
              {AVISO_BIOIMPEDANCIA}
            </p>
          </section>
        </div>
      </div>

      <div className="d-rejilla d-rejilla--2">
        <section className="tarjeta">
          <div className="fila fila--entre" style={{ marginBottom: 'var(--e4)' }}>
            <span className="eyebrow">Plan</span>
            <button type="button" className="boton boton--chico boton--fantasma" onClick={() => ir('calendario')}>
              Calendario
            </button>
          </div>

          {v.calendario && v.plan ? (
            <>
              <PilaDePlan
                usadas={v.plan.usadas}
                pendientes={v.plan.restantes}
                enRiesgo={v.calendario.sesionesEnRiesgo}
                alto={34}
              />

              <div className="d-rejilla d-rejilla--4" style={{ marginTop: 'var(--e4)' }}>
                <div>
                  <span className="eyebrow">Usadas</span>
                  <Cifra valor={v.plan.usadas} decimales={0} />
                </div>
                <div>
                  <span className="eyebrow">Quedan</span>
                  <Cifra valor={v.plan.restantes} decimales={0} />
                </div>
                <div>
                  <span className="eyebrow">Días hábiles</span>
                  <Cifra valor={v.calendario.diasHabiles} decimales={0} />
                </div>
                <div>
                  <span className="eyebrow">Colchón</span>
                  <span
                    className="cifra-grande"
                    style={{ color: v.calendario.colchonCritico ? 'var(--ambar)' : undefined }}
                  >
                    {v.calendario.colchon}
                  </span>
                </div>
              </div>

              <hr className="separador" />

              <span className="eyebrow">Próximas sesiones</span>
              <table className="tabla" style={{ marginTop: 6 }}>
                <tbody>
                  {v.calendario.entradas.slice(0, 5).map((e) => (
                    <tr key={e.fecha}>
                      <td className="lectura" style={{ width: 90 }}>
                        {e.fecha.slice(5)}
                      </td>
                      <td className="apagado" style={{ width: 80, textTransform: 'capitalize' }}>
                        {nombreDia(e.fecha)}
                      </td>
                      <td>{e.nombre}</td>
                      <td style={{ textAlign: 'right' }}>
                        {e.fijada && <Insignia>ya agendada</Insignia>}
                        {e.conflicto && <Insignia tono="ambar">músculo repetido</Insignia>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {v.calendario.ultimaFechaUtil && (
                <p className="nota--fina" style={{ marginTop: 'var(--e3)' }}>
                  Último día en que se puede entrenar: {fechaLarga(v.calendario.ultimaFechaUtil)}.
                </p>
              )}
            </>
          ) : (
            <Vacio>Todavía no hay un plan cargado.</Vacio>
          )}
        </section>

        <section className="columna" style={{ gap: 'var(--e3)' }}>
          {/* Sin el botón de sesión que vivía acá: subió a la franja de hoy, arriba de todo. */}
          <span className="eyebrow">Para mirar</span>

          {v.alertas.length === 0 ? (
            <Aviso tono="info">Sin alertas. Seguí registrando.</Aviso>
          ) : (
            v.alertas.map((a) => (
              <Aviso
                key={a.clave}
                tono={a.severidad === 'importante' ? 'importante' : 'atencion'}
                titulo={a.titulo}
              >
                {a.detalle}
                {a.accion && (
                  <>
                    <br />
                    <span className="apagado">{a.accion}</span>
                  </>
                )}
              </Aviso>
            ))
          )}

          {/* Dato del registro, no un reproche (§10): la app no dice qué comer ni cuánto, sólo que
              hace días que no hay nada anotado y que los promedios se apoyan en menos días. */}
          {v.diasSinAnotarComida !== null && (
            <div className="fila fila--entre" style={{ gap: 'var(--e3)' }}>
              <span className="nota">
                Hace {v.diasSinAnotarComida} {v.diasSinAnotarComida === 1 ? 'día' : 'días'} que no
                anotás comida.
              </span>
              <button
                type="button"
                className="boton boton--chico"
                style={{ flex: 'none' }}
                onClick={() => ir('nutricion')}
              >
                Anotar
              </button>
            </div>
          )}

          <section className="tarjeta">
            <span className="eyebrow">Series efectivas por grupo · 7 días</span>
            <table className="tabla" style={{ marginTop: 'var(--e2)' }}>
              <thead>
                <tr>
                  <th>Grupo</th>
                  <th style={{ width: '45%' }}>Semana</th>
                  <th className="num">Series</th>
                  <th className="num">Últ. estímulo</th>
                </tr>
              </thead>
              <tbody>
                {v.semana.celdas.map((c) => (
                  <tr key={c.grupo}>
                    <td style={{ textTransform: 'capitalize' }}>{c.grupo}</td>
                    <td>
                      {/* El rojo de la pila es para el abandono de verdad. `abandonado` de core
                          también viene en true el primer día, cuando nadie entrenó nada todavía:
                          pintar de rojo la semana entera de una cuenta nueva es retar por algo
                          imposible de haber hecho (§10). */}
                      <Pila
                        estados={estadosDeRatio(
                          c.objetivo ? c.seriesEfectivas / c.objetivo : Math.min(1, c.seriesEfectivas / 12),
                          12,
                          c.estimulo.alarma ? 'alerta' : 'llena',
                        )}
                        alto={12}
                      />
                    </td>
                    <td className="num">
                      {formatearNumero(c.seriesEfectivas, 1)}
                      {c.objetivo ? <span className="apagado"> / {c.objetivo}</span> : null}
                    </td>
                    <td className="num">
                      {c.diasSinEstimulo === null ? (
                        <span className="apagado">{c.estimulo.texto}</span>
                      ) : (
                        c.estimulo.texto
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="nota--fina" style={{ marginTop: 'var(--e2)' }}>
              El primario cuenta entero y el secundario a mitad. El "último estímulo" mira sólo
              trabajo primario: que un back extension toque isquios no es entrenar pierna.
            </p>
          </section>
        </section>
      </div>
    </>
  );
}

function Tarjetita({
  etiqueta,
  valor,
  unidad,
  decimales = 1,
  pie,
  alerta = false,
}: {
  etiqueta: string;
  valor: number | null;
  unidad: string;
  decimales?: number;
  pie?: string | undefined;
  alerta?: boolean;
}): ReactNode {
  return (
    <div className="tarjeta">
      <span className="eyebrow">{etiqueta}</span>
      <div style={{ color: alerta ? 'var(--ambar)' : undefined }}>
        <Cifra valor={valor} unidad={unidad} decimales={decimales} />
      </div>
      {pie && <p className="nota--fina">{pie}</p>}
    </div>
  );
}
